import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import DoctorPatientPicker from "./DoctorPatientPicker";
import { getDoctorByUserId } from "./patientApi";
import { supportHomePath, supportDoctorPath, todayIsoDate } from "./supportUrls";

const SupportDoctorPage = () => {
  const { doctorId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const decodedDoctorId = decodeURIComponent(doctorId || "");
  const doctorFromNavigation = location.state?.doctor;

  const dateParam = searchParams.get("date");
  const doctorDate = dateParam || todayIsoDate();

  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [doctorLoadError, setDoctorLoadError] = useState(null);
  const [isLoadingDoctor, setIsLoadingDoctor] = useState(true);

  useEffect(() => {
    if (!dateParam) {
      setSearchParams({ date: todayIsoDate() }, { replace: true });
    }
  }, [dateParam, setSearchParams]);

  useEffect(() => {
    if (!decodedDoctorId) {
      setDoctorLoadError("Doctor not specified.");
      setIsLoadingDoctor(false);
      return;
    }

    if (
      doctorFromNavigation &&
      String(doctorFromNavigation.user_id).toLowerCase() === decodedDoctorId.toLowerCase()
    ) {
      setSelectedDoctor(doctorFromNavigation);
      setDoctorLoadError(null);
      setIsLoadingDoctor(false);
      return;
    }

    let cancelled = false;

    (async () => {
      setIsLoadingDoctor(true);
      setDoctorLoadError(null);

      const data = await getDoctorByUserId(decodedDoctorId);
      if (cancelled) return;

      if (data.error) {
        setSelectedDoctor(null);
        setDoctorLoadError(data.error);
      } else {
        setSelectedDoctor(data);
      }

      setIsLoadingDoctor(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [decodedDoctorId, doctorFromNavigation]);

  const handleDateChange = (newDate) => {
    navigate(supportDoctorPath(decodedDoctorId, newDate), {
      state: doctorFromNavigation ? { doctor: selectedDoctor } : undefined,
    });
  };

  const handleChangeDoctor = () => {
    navigate(supportHomePath("doctor"));
  };

  if (isLoadingDoctor) {
    return (
      <div className="flex flex-col w-full max-w-5xl mx-auto">
        <h1 className="font-bold text-[28px] text-ktx-purple mt-8">Customer Support</h1>
        <p className="mt-6 text-sm text-gray-500">Loading doctor...</p>
      </div>
    );
  }

  if (!selectedDoctor) {
    return (
      <div className="flex flex-col w-full max-w-5xl mx-auto">
        <h1 className="font-bold text-[28px] text-ktx-purple mt-8">Customer Support</h1>
        <p className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {doctorLoadError || "Doctor not found."}
        </p>
        <Link
          to={supportHomePath("doctor")}
          className="mt-4 text-ktx-purple hover:underline font-medium text-sm w-fit"
        >
          Back to search
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full max-w-5xl mx-auto">
      <h1 className="font-bold text-[28px] text-ktx-purple mt-8">Customer Support</h1>

      <div className="mt-6">
        <DoctorPatientPicker
          doctor={selectedDoctor}
          doctorDate={doctorDate}
          onDateChange={handleDateChange}
          onChangeDoctor={handleChangeDoctor}
        />
      </div>
    </div>
  );
};

export default SupportDoctorPage;
